"use client";

import Link from "next/link";
import {
  ArrowLeft,
  ArrowUpLeft,
  Sparkles,
  PhoneCall,
  MapPin,
  Heart,
  Shield,
  CheckCircle2,
  Star,
  Cpu,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { useAppLocale, useT } from "@/i18n/context";
import { ChannelLogo } from "./ChannelLogo";

export function SiteFooter() {
  const t = useT();
  const locale = useAppLocale();
  const year = new Intl.DateTimeFormat(locale === "fa" ? "fa-IR" : "en-US", {
    year: "numeric",
  }).format(new Date());

  const cols = [
    {
      title: t("footer.columns.product", "محصول"),
      links: [
        t("common.nav.featuresList", "ویژگی‌ها"),
        t("common.nav.integrations", "یکپارچه‌سازی"),
        t("common.nav.ai", "هوش مصنوعی"),
        t("footer.columns.roadmap", "نقشهٔ راه"),
      ],
    },
    {
      title: t("footer.columns.resources", "منابع"),
      links: [
        t("footer.columns.docs", "مستندات"),
        t("footer.columns.blog", "وبلاگ"),
        t("footer.columns.tutorials", "آموزش‌ها"),
        "API",
      ],
    },
    {
      title: t("footer.columns.company", "شرکت"),
      links: [
        t("common.nav.about", "درباره ما"),
        t("common.nav.contactShort", "تماس"),
        t("common.nav.privacy", "حریم خصوصی"),
        t("common.nav.terms", "قوانین"),
      ],
    },
  ];
  return (
    <footer className="relative pt-20 overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary-main/50 to-transparent" />
      <div className="absolute -top-20 right-1/4 w-[420px] h-[420px] rounded-full bg-primary-main/10 blur-[140px] pointer-events-none" />
      <div className="absolute -top-20 left-1/4 w-[420px] h-[420px] rounded-full bg-secondary-main/10 blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-6 relative">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end mb-14 pb-12 border-b border-white/10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-main/15 border border-primary-main/30 text-[11px] text-primary-main mb-5">
              <Sparkles size={12} /> Trendi Smart
            </div>
            <h3 className="text-3xl md:text-5xl font-black leading-[1.15] mb-5 text-white">
              <span>{t("footer.ctaTitle", "مغز هوشمند تعاملات")}</span>{" "}
              <span className="brand-gradient-text">
                {t("footer.ctaTitleHighlight", "کسب‌وکار شما")}
              </span>
            </h3>
            <p className="text-grayScale-400 text-sm leading-relaxed max-w-lg">
              {t(
                "footer.ctaDescription",
                "همهٔ کانال‌های ارتباطی شما در یک داشبورد، با یک مغز هوش مصنوعی ایرانی که فارسی را عمیقاً درک می‌کند."
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link
              href="/auth"
              className="group px-6 py-3 rounded-full bg-primary-main text-white font-black text-sm flex items-center gap-2 hover:gap-3 transition-all shadow-lg shadow-primary-main/30"
            >
              {t("common.auth.startFree", "شروع رایگان")}
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            </Link>
            <a
              href="tel:02191000000"
              className="px-6 py-3 rounded-full border border-white/15 text-sm font-bold text-white flex items-center gap-2 hover:bg-white/5 transition-colors"
            >
              <PhoneCall size={14} />
              {t("footer.phone", "۰۲۱-۹۱۰۰۰۰۰۰")}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 mb-12">
          <div className="col-span-2 md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <BrandMark size={42} animated={false} />
              <div className="leading-none">
                <div className="font-black text-base text-white">
                  {t("common.brandName", "ترندی اسمارت")}
                </div>
                <div className="text-[10px] text-grayScale-500 mt-1">trendismart.com</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mb-4">
              {["Instagram", "Telegram", "WhatsApp", "Bale", "Rubika", "Eitaa"].map((n) => (
                <div
                  key={n}
                  className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center hover:border-primary-main/40 hover:bg-white/[0.08] transition-all"
                >
                  <ChannelLogo name={n} className="w-4 h-4" />
                </div>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] text-grayScale-400">
              <span className="w-1.5 h-1.5 rounded-full bg-alert-success-main animate-pulse" />
              {t("footer.supportActive", "پشتیبانی ۲۴/۷ فعال")}
              <span className="text-grayScale-600">•</span>
              <MapPin size={11} className="text-primary-main" />
              {t("footer.address", "تهران، ولیعصر")}
            </div>
          </div>

          {cols.map((c, i) => (
            <div key={i} className="md:col-span-2">
              <h4 className="font-black text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-white">
                <span className="w-1 h-3 rounded-full bg-primary-main" />
                {c.title}
              </h4>
              <ul className="space-y-2.5">
                {c.links.map((l, j) => (
                  <li key={j}>
                    <a
                      href="#"
                      className="group text-sm text-grayScale-400 hover:text-primary-main transition-colors inline-flex items-center gap-1.5"
                    >
                      <span>{l}</span>
                      <ArrowUpLeft
                        size={11}
                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary-main"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="col-span-2 md:col-span-2">
            <h4 className="font-black text-xs uppercase tracking-[0.15em] mb-4 flex items-center gap-2 text-white">
              <span className="w-1 h-3 rounded-full bg-primary-main" />
              {t("footer.badges.title", "نمادها")}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <TrustBadge icon={Shield} label={t("footer.badges.trust", "نماد اعتماد")} sub="enamad" />
              <TrustBadge
                icon={CheckCircle2}
                label={t("footer.badges.samandehi", "ساماندهی")}
                sub="samandehi"
              />
              <TrustBadge
                icon={Star}
                label={t("footer.badges.business", "کسب‌وکار")}
                sub={t("footer.badges.businessSub", "رسمی")}
              />
              <TrustBadge icon={Cpu} label="ISO 27001" sub={t("footer.badges.isoSub", "امنیت")} />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-6 pb-8 border-t border-white/10">
          <p className="text-xs text-grayScale-500">
            {t("footer.copyright", "© {year} ترندی اسمارت — تمام حقوق محفوظ است.", { year })}
          </p>
          <div className="flex items-center gap-3 text-xs text-grayScale-500">
            <span className="flex items-center gap-1.5">
              <Heart size={12} className="text-alert-error-main" />
              {t("footer.madeInIran", "ساخته‌شده در ایران")}
            </span>
            <span className="w-1 h-1 rounded-full bg-grayScale-700" />
            <a href="#" className="hover:text-primary-main transition-colors">
              {t("common.nav.privacy", "حریم خصوصی")}
            </a>
            <span className="w-1 h-1 rounded-full bg-grayScale-700" />
            <a href="#" className="hover:text-primary-main transition-colors">
              {t("common.nav.terms", "قوانین")}
            </a>
          </div>
        </div>

        <div className="relative -mb-4 sm:-mb-8 pointer-events-none select-none overflow-hidden">
          <div className="text-[14vw] leading-[0.85] font-black text-center bg-gradient-to-b from-white/[0.06] via-white/[0.02] to-transparent bg-clip-text text-transparent tracking-tighter">
            TRENDISMART
          </div>
        </div>
      </div>
    </footer>
  );
}

function TrustBadge({ icon: Icon, label, sub }: { icon: any; label: string; sub: string }) {
  return (
    <div className="aspect-square rounded-xl bg-white/[0.04] border border-white/10 flex flex-col items-center justify-center gap-1 hover:border-primary-main/40 hover:bg-white/[0.07] transition-all p-2">
      <Icon size={18} className="text-primary-main" />
      <div className="text-[10px] font-black text-center leading-tight text-white">{label}</div>
      <div className="text-[8px] text-grayScale-500">{sub}</div>
    </div>
  );
}

export default SiteFooter;
